import {
  apiErrorMessage,
  createQuestionnaire,
  fetchQuestionnaireAlerts,
  fetchQuestionnaireTourOptions,
  fetchQuestionnaires,
  saveQuestionnaireAlerts,
  setQuestionnaireStatus,
  updateQuestionnaire
} from '@api/taptotip/questionnaire';
import TopNav from '@components/Nav/TopNav';
import TipDirectLeftMenu from '@components/Nav/TopNav/TipDirectLeftMenu';
import { StyleBreakpoints } from '@constants';
import { formatDayFirst } from '@utils/dayFirstDates';
import { makeStyles } from '@material-ui/core/styles';
import { isQuestionnairesEnabledForCompany } from '@utils/helpers';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import AlertSettings from './AlertSettings';
import ElementDetail from './ElementDetail';
import Insights from './Insights';
import PrivacySettings from './PrivacySettings';
import Responses from './Responses';
import Editor, { CARD } from './Editor';
import { blankDraft, duplicateDraft, fromQuestionnaire, standardDraft, toPayload } from './draft';

/**
 * Card 324 — head office's Guest Feedback page: build questionnaires and
 * decide who hears about the answers, and (card 325, the default tab) read
 * what guests said.
 *
 * Company admins only (the route is adminOnly and the API refuses anyone
 * else), and only while the company's questionnaires switch is on. Guides get
 * the QR page instead (card 323).
 */

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',

    [theme.breakpoints.up(StyleBreakpoints.sm)]: {
      flexDirection: 'row'
    }
  }
}));

export const TABS = [
  { key: 'responses', label: 'Responses' },
  // Card 338.
  { key: 'insights', label: 'Insights' },
  { key: 'questionnaires', label: 'Questionnaires' },
  { key: 'alerts', label: 'Alerts' }
];

const formatDate = value =>
  formatDayFirst(value, { day: 'numeric', month: 'short', year: 'numeric' });

const QuestionnaireList = ({
  questionnaires,
  tourOptions,
  onEdit,
  onNew,
  onDuplicate,
  onStatus,
  busyId
}) => {
  if (questionnaires.length === 0) {
    return (
      <section className={CARD} data-testid="questionnaires-empty">
        <h2 className="text-base font-black text-slate-900 m-0">No questionnaire yet</h2>
        <p className="text-sm text-slate-500 m-0 mt-1 mb-5 max-w-[60ch]">
          Start from our standard guest feedback form in English and French and reword it, or build
          one from scratch.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            type="button"
            onClick={() => onNew(standardDraft())}
            data-testid="new-standard"
            className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-bold border-0 rounded-xl px-4 py-2.5 cursor-pointer">
            Start from the standard form
          </button>
          <button
            type="button"
            onClick={() => onNew(blankDraft())}
            data-testid="new-blank"
            className="bg-white text-indigo-700 text-sm font-bold border border-solid border-indigo-200 rounded-xl px-4 py-2.5 cursor-pointer">
            Start blank
          </button>
        </div>
      </section>
    );
  }

  const tourCount = q => q.tours.length;
  return (
    <div className="flex flex-col gap-4">
      {questionnaires.map(q => {
        const active = q.status === 'active';
        const questions = q.sections.reduce((n, s) => n + s.questions.length, 0);
        const names = q.tours
          .map(t => (tourOptions.find(o => o.id === String(t.tourId)) || {}).name)
          .filter(Boolean);
        return (
          <section key={q._id} className={CARD} data-testid={`questionnaire-${q._id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-base font-black text-slate-900 m-0 flex items-center gap-2">
                  {q.name}
                  <span
                    className={`text-xs font-bold rounded-lg px-2 py-0.5 ${
                      active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                    }`}>
                    {active ? 'Active' : 'Archived'}
                  </span>
                </h2>
                <p className="text-sm text-slate-500 m-0 mt-1">
                  {[
                    `${questions} ${questions === 1 ? 'question' : 'questions'}`,
                    tourCount(q) === 0
                      ? 'no tours yet'
                      : `${tourCount(q)} ${tourCount(q) === 1 ? 'tour' : 'tours'}`,
                    q.locales.includes('fr') ? 'English and French' : 'English',
                    `edited ${formatDate(q.updatedAt)}`
                  ].join(', ')}
                </p>
                {names.length > 0 && (
                  <p className="text-sm text-slate-700 m-0 mt-2">{names.join(', ')}</p>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => onEdit(q)}
                  data-testid={`edit-${q._id}`}
                  className="text-sm font-bold rounded-xl px-3.5 py-2 cursor-pointer border border-solid border-indigo-200 bg-white text-indigo-700">
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => onDuplicate(q)}
                  className="text-sm font-bold rounded-xl px-3.5 py-2 cursor-pointer border border-solid border-slate-200 bg-white text-slate-600">
                  Duplicate
                </button>
                <button
                  type="button"
                  disabled={busyId === q._id}
                  onClick={() => onStatus(q, active ? 'archived' : 'active')}
                  data-testid={`status-${q._id}`}
                  className="text-sm font-bold rounded-xl px-3.5 py-2 cursor-pointer border border-solid border-slate-200 bg-white text-slate-600 disabled:opacity-50">
                  {active ? 'Archive' : 'Restore'}
                </button>
              </div>
            </div>
          </section>
        );
      })}
      <button
        type="button"
        onClick={() => onNew(blankDraft())}
        className="self-start text-sm font-bold text-indigo-700 bg-transparent border-0 cursor-pointer p-0 hover:underline">
        New questionnaire
      </button>
    </div>
  );
};

QuestionnaireList.propTypes = {
  questionnaires: PropTypes.array.isRequired,
  tourOptions: PropTypes.array.isRequired,
  onEdit: PropTypes.func,
  onNew: PropTypes.func,
  onDuplicate: PropTypes.func,
  onStatus: PropTypes.func,
  busyId: PropTypes.string
};

export const QuestionnaireAdmin = ({ user, initialTab = 'responses' }) => {
  const classes = useStyles();
  const enabled = isQuestionnairesEnabledForCompany(user);

  const [tab, setTab] = useState(initialTab);
  // Insights drill-down: Responses opens with these filters and says why.
  const [drill, setDrill] = useState(null);
  // A hotel or experience opened from Insights, shown as its own page.
  const [element, setElement] = useState(null);
  const [insightScope, setInsightScope] = useState({ tourId: '', period: 90 });
  const keepScope = useCallback(scope => setInsightScope(scope), []);
  const [status, setStatus] = useState('loading');
  const [questionnaires, setQuestionnaires] = useState([]);
  const [tourOptions, setTourOptions] = useState([]);
  const [alerts, setAlerts] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  const [listError, setListError] = useState('');

  const load = useCallback(() => {
    setStatus('loading');
    return Promise.all([
      fetchQuestionnaires(),
      fetchQuestionnaireTourOptions(),
      fetchQuestionnaireAlerts()
    ])
      .then(([list, options, alertSettings]) => {
        setQuestionnaires((list && list.questionnaires) || []);
        setTourOptions((options && options.tours) || []);
        setAlerts(alertSettings);
        setStatus('ready');
      })
      .catch(() => setStatus('error'));
  }, []);

  useEffect(() => {
    if (enabled) load();
  }, [enabled, load]);

  const saveDraft = draft => {
    setBusy(true);
    setError('');
    const payload = toPayload(draft);
    const request = draft.id
      ? updateQuestionnaire(draft.id, payload)
      : createQuestionnaire(payload);
    request
      .then(() => {
        setEditing(null);
        return load();
      })
      .catch(err => setError(apiErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  const changeStatus = (q, next) => {
    setBusyId(q._id);
    setListError('');
    setQuestionnaireStatus(q._id, next)
      .then(load)
      .catch(err => setListError(apiErrorMessage(err)))
      .finally(() => setBusyId(null));
  };

  const saveAlerts = form => {
    setBusy(true);
    setError('');
    saveQuestionnaireAlerts(form)
      .then(saved => setAlerts(saved))
      .catch(err => setError(apiErrorMessage(err)))
      .finally(() => setBusy(false));
  };

  const renderBody = () => {
    if (status === 'loading') {
      return (
        <div className={CARD} role="status" aria-label="Loading">
          <div className="h-5 bg-slate-100 rounded w-1/3" />
          <div className="h-4 bg-slate-100 rounded w-2/3 mt-3" />
        </div>
      );
    }
    if (status === 'error') {
      return (
        <div className={CARD}>
          <p className="text-slate-600 m-0 mb-4">
            We couldn&rsquo;t load your guest feedback settings.
          </p>
          <button
            type="button"
            onClick={load}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white border-0 cursor-pointer">
            Try again
          </button>
        </div>
      );
    }
    if (editing) {
      return (
        <Editor
          initialDraft={editing}
          tourOptions={tourOptions}
          busy={busy}
          error={error}
          onSave={saveDraft}
          onCancel={() => {
            setEditing(null);
            setError('');
          }}
        />
      );
    }
    if (element) {
      return <ElementDetail element={element} onBack={() => setElement(null)} />;
    }
    if (tab === 'responses') {
      return (
        <Responses
          key={drill ? drill.key : 'all'}
          onOpenInsights={() => setTab('insights')}
          initialFilters={drill ? drill.filters : undefined}
          drillLabel={drill ? drill.label : undefined}
          onClearDrill={() => setDrill(null)}
        />
      );
    }
    if (tab === 'insights') {
      return (
        <Insights
          initialScope={insightScope}
          onScopeChange={keepScope}
          onOpenElement={setElement}
          onDrill={({ filters, label }) => {
            setDrill({ filters, label, key: Date.now() });
            setTab('responses');
          }}
        />
      );
    }
    if (tab === 'alerts') {
      return <AlertSettings alerts={alerts} busy={busy} error={error} onSave={saveAlerts} />;
    }
    return (
      <>
        {listError && (
          <p
            className="text-sm text-rose-700 bg-rose-50 border border-solid border-rose-200 rounded-xl p-3 m-0"
            role="alert">
            {listError}
          </p>
        )}
        <QuestionnaireList
          questionnaires={questionnaires}
          tourOptions={tourOptions}
          busyId={busyId}
          onNew={draft => setEditing(draft)}
          onEdit={q => setEditing(fromQuestionnaire(q))}
          onDuplicate={q => setEditing(duplicateDraft(fromQuestionnaire(q)))}
          onStatus={changeStatus}
        />
        <PrivacySettings />
      </>
    );
  };

  // After every hook: a typed URL must not render the page for a company that
  // is not switched on (the nav already hides the tab).
  if (!enabled) return <Redirect to="/" />;

  return (
    <>
      <TopNav />
      <div className={classes.container}>
        <TipDirectLeftMenu user={user} selectedTab="GuestFeedback" />
        <div className="flex flex-col gap-6 w-full p-4 sm:p-8 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900 m-0">Guest feedback</h1>
          {!editing && !element && (
            // One line on every phone: the labels never break mid-word, and
            // the narrowest screens scroll the row instead. The divider is an
            // inset shadow so the active tab's underline can sit on it inside
            // the scroller (2026-10-07).
            <div
              className="flex gap-3 sm:gap-5 overflow-x-auto shadow-[inset_0_-1px_0_#e2e8f0] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              role="tablist">
              {TABS.map(t => (
                <button
                  key={t.key}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.key}
                  onClick={() => {
                    setTab(t.key);
                    setError('');
                    // Choosing a tab yourself starts from the full picture.
                    setDrill(null);
                  }}
                  data-testid={`tab-${t.key}`}
                  className={`flex-none whitespace-nowrap text-[13px] sm:text-sm font-bold bg-transparent border-0 border-b-2 border-solid px-1 pb-2 cursor-pointer ${
                    tab === t.key
                      ? 'text-indigo-700 border-indigo-600'
                      : 'text-slate-500 border-transparent'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          )}
          {renderBody()}
        </div>
      </div>
    </>
  );
};

QuestionnaireAdmin.propTypes = {
  user: PropTypes.object,
  initialTab: PropTypes.string
};

const mapStateToProps = state => ({
  user: state.user?.user
});

export default connect(mapStateToProps)(QuestionnaireAdmin);
